// [S-060 / S-060a] Top-level router. Anonymous → Login; authenticated →
// the application shell at /home (and stub routes for sidebar nav targets
// not yet built, which 404 to a friendly empty state per S-060a spec).
import { useEffect } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useAuth, useSession } from "./auth/AuthProvider";
import { ActiveTenantProvider } from "./auth/ActiveTenantProvider";
import { SessionExpiredModal } from "./auth/SessionExpiredModal";
import { useTenantPreferences } from "./data/tenantPreferencesQuery";
import { applyBrandingToDocument } from "./screens/Settings";
import { RequireCapability, RequireAuth } from "./auth/RequireCapability";
import { AppShell } from "./shell/AppShell";
import { LoadingState } from "./components";
import { Home } from "./screens/Home";
import { BAHome } from "./screens/BAHome";
import { Login } from "./screens/Login";
import { BriefCompose } from "./screens/BriefCompose";
import { BriefDetail } from "./screens/BriefDetail";
import { Briefs } from "./screens/Briefs";
import { Drafts } from "./screens/Drafts";
import { DraftDetail } from "./screens/DraftDetail";
import { Precedents } from "./screens/Precedents";
import { PrecedentDetail } from "./screens/PrecedentDetail";
import { Templates } from "./screens/Templates";
import { TemplateAuthor } from "./screens/TemplateAuthor";
import { TemplateEdit } from "./screens/TemplateEdit";
import { TemplateDetail } from "./screens/TemplateDetail";
import { TemplateVariables } from "./screens/TemplateVariables";
import { TemplateManagement } from "./screens/TemplateManagement";
import { Marketplace } from "./screens/Marketplace";
import { TemplateBulkUpload } from "./screens/TemplateBulkUpload";
import { CertificationQueue } from "./screens/CertificationQueue";
import { Activity } from "./screens/Activity";
import { IndexHealth } from "./screens/IndexHealth";
import { Attestations } from "./screens/Attestations";
import { AttestationDetail } from "./screens/AttestationDetail";
import { Classification } from "./screens/Classification";
import { TenantClassification } from "./screens/TenantClassification";
import { PerksRegister } from "./screens/PerksRegister";
import { PlatformAtlas } from "./screens/PlatformAtlas";
import { Evals } from "./screens/Evals";
import { ReAttestation } from "./screens/ReAttestation";
import { Tenants } from "./screens/Tenants";
import { TenantUsers } from "./screens/TenantUsers";
import { Settings } from "./screens/Settings";
import { Support, SupportTickets } from "./screens/Support";
import { Upload } from "./screens/Upload";
import { Welcome } from "./screens/Welcome";
import { SetPassword } from "./screens/SetPassword";
import { Ingestion } from "./screens/Ingestion";
import { ReviewQueue } from "./screens/ReviewQueue";
import { Analytics } from "./screens/Analytics";
import { PersonaManagement } from "./screens/PersonaManagement";
import { PersonaDetail } from "./screens/persona/PersonaDetail";
import { McpConsent, McpConsentLoginRedirect } from "./screens/McpConsent";
import { NotYetBuilt } from "./shell/NotYetBuilt";

export function App(): JSX.Element {
  const { state } = useAuth();

  // While the persisted session is being restored on load, render a neutral
  // placeholder rather than the login routes — otherwise every refresh flashes
  // (and navigates to) /login before restore resolves.
  if (state.status === "initializing") {
    return (
      <div
        style={{ display: "grid", placeItems: "center", minHeight: "100vh" }}
        aria-busy="true"
      >
        <LoadingState label="Loading…" />
      </div>
    );
  }

  // [S-637 slice 2] `expired` renders the SAME authed tree as `authenticated`
  // (never the Login routes), so a lapsed session mid-task keeps every screen
  // mounted — component state survives — while <SessionExpiredModal> overlays
  // the recovery prompt. The tree unmounts only on a real sign-out (anonymous).
  const authed =
    state.status === "authenticated" || state.status === "expired";

  if (!authed) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        {/* S-091a — anonymous post-password-reset landing. Identity
            Platform sends the invited user here after they finish the
            reset flow; this screen tells them what just happened and
            CTAs to /login. */}
        <Route path="/welcome" element={<Welcome />} />
        {/* S-408 — anonymous invited user sets their password here (our own
            page, not IP's hosted reset). The token rides in ?token=. */}
        <Route path="/set-password" element={<SetPassword />} />
        {/* S-475 — claude.ai MCP connector lands here before sign-in; stash the
            OAuth params + return URL, then route through /login. */}
        <Route path="/mcp-consent" element={<McpConsentLoginRedirect />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    // S-411 — ActiveTenantProvider owns the active-tenant selection + the user's
    // memberships. It wraps the whole authed tree so useSession() inside it
    // derives the effective per-tenant role and every screen + the branding boot
    // follow a tenant switch.
    <ActiveTenantProvider>
      <BrandingBoot />
      {/* [S-637 slice 2] Mounts once inside the authed tree; renders its overlay
          only while state is `expired`. Recovery happens in place. */}
      <SessionExpiredModal />
      <AppShell>
        <Routes>
          <Route path="/" element={<Navigate to="/home" replace />} />
        {/* Once authenticated, /login is a stale URL — redirect rather
            than dump the user into the NotYetBuilt catch-all. */}
        <Route path="/login" element={<Navigate to="/home" replace />} />
        {/* S-091a — authenticated users who somehow land on /welcome
            (e.g. an admin testing the invite flow on their own
            account) bounce to /home rather than see the post-reset
            explainer. The Welcome screen itself also redirects when
            authenticated, but routing it here keeps the URL surface
            consistent with the anonymous case. */}
        <Route path="/welcome" element={<Navigate to="/home" replace />} />
        <Route path="/home" element={<Home />} />
        {/* [S-094] BA persona home — 1-click into precedent + templates. */}
        <Route path="/ba-home" element={<BAHome />} />
        {/* [S-710 / E-PERSONA-ADMIN] Persona configuration — platform_admin
            only: edits how a persona behaves (prompt delta / tools / model /
            limits). The standalone per-persona chat surface (Studio Agents,
            /agents) was retired once Home's routing (S-666) + the assistant
            persona's own inline tools covered every specialist. */}
        <Route
          path="/admin/personas"
          element={
            <RequireCapability capability="agent:manage" scope="platform">
              <PersonaManagement />
            </RequireCapability>
          }
        />
        <Route
          path="/admin/personas/:kind"
          element={
            <RequireCapability capability="agent:manage" scope="platform">
              <PersonaDetail />
            </RequireCapability>
          }
        />
        {/* [S-475] OAuth consent for the claude.ai MCP connector. Any signed-in
            user (tenant_user+) can authorize their own connector. */}
        <Route
          path="/mcp-consent"
          element={
            <RequireAuth>
              <McpConsent />
            </RequireAuth>
          }
        />
        <Route path="/library" element={<Templates />} />
        <Route
          path="/library/new"
          element={
            <RequireCapability capability="template:author">
              <TemplateAuthor />
            </RequireCapability>
          }
        />
        <Route
          path="/library/:templateId/edit"
          element={
            <RequireCapability capability="template:author">
              <TemplateEdit />
            </RequireCapability>
          }
        />
        <Route
          path="/library/:templateId/variables"
          element={
            <RequireCapability capability="template:tag">
              <TemplateVariables />
            </RequireCapability>
          }
        />
        <Route path="/library/:templateId" element={<TemplateDetail />} />
        <Route path="/precedent" element={<Precedents />} />
        <Route path="/precedent/:id" element={<PrecedentDetail />} />
        <Route path="/briefs" element={<Briefs />} />
        {/* [S-385] Draft Workspace — tenant-scoped. Access control (owner /
            collaborator, same-tenant share) is enforced by the backend; the
            backend rejects platform_admin (no tenant) with 403. */}
        <Route path="/drafts" element={<Drafts />} />
        <Route path="/drafts/:draftId" element={<DraftDetail />} />
        <Route path="/briefs/new" element={<BriefCompose />} />
        <Route path="/briefs/:briefId" element={<BriefDetail />} />
        <Route path="/activity" element={<Activity />} />
        <Route path="/index-health" element={<IndexHealth />} />
        {/* Wireframe sidebar entries whose React screens haven't shipped
            yet — point at the NotYetBuilt empty state with the story id
            that's owed, so the navigation graph stays complete and the
            gap between visual contract and current implementation is
            honest to the user. */}
        <Route
          path="/attestation"
          element={
            <RequireCapability capability="attestation:access">
              <Attestations />
            </RequireCapability>
          }
        />
        <Route
          path="/attestation/:templateId"
          element={
            <RequireCapability capability="attestation:access">
              <AttestationDetail />
            </RequireCapability>
          }
        />
        <Route
          path="/analytics"
          element={
            <RequireCapability capability="analytics:view_tenant">
              <Analytics />
            </RequireCapability>
          }
        />
        <Route
          path="/ingestion"
          element={
            // Platform-wide ingestion status board — platform staff only. No
            // dedicated capability names it; gate on the platform marker cap at
            // platform scope (only a principal with platform reach passes).
            <RequireCapability
              capability="platform:manage_tenants"
              scope="platform"
            >
              <Ingestion />
            </RequireCapability>
          }
        />
        <Route
          path="/queue"
          element={
            // S-411 — tenant_admin reviews their OWN tenant's queue; the route
            // scopes to the caller's (active) tenant and the backend gates each
            // approve/reject via review:moderate (studioId-scoped), so a
            // tenant_admin can never see or act on another tenant's docs.
            // platform_admin still sees all tenants.
            <RequireCapability capability="review:moderate">
              <ReviewQueue />
            </RequireCapability>
          }
        />
        <Route path="/upload" element={<Upload />} />
        {/* [S-263] Studio Archive was merged into the Upload Portal as its
            "All uploads" tab — the durable status board is the operational tail
            of uploading, not a peer of the readable Precedent library. Keep the
            old path as a redirect so existing deep-links land on the tab. */}
        <Route
          path="/archive"
          element={<Navigate to="/upload?tab=all" replace />}
        />
        <Route
          path="/settings"
          element={
            <RequireCapability capability="tenant:manage">
              <Settings />
            </RequireCapability>
          }
        />
        {/* Support portal. Every member (tenant:read) files tickets and reads
            back their own; platform reach sees every tenant's. The backend
            does the scoping. */}
        <Route
          path="/support"
          element={
            <RequireCapability capability="tenant:read">
              <Support />
            </RequireCapability>
          }
        />
        <Route
          path="/support/tickets"
          element={
            <RequireCapability capability="tenant:read">
              <SupportTickets />
            </RequireCapability>
          }
        />
        <Route
          path="/reattestation"
          element={
            <RequireCapability
              capability="reattestation:manage"
              scope="platform"
            >
              <ReAttestation />
            </RequireCapability>
          }
        />
        <Route
          path="/platform/evals"
          element={
            // [S-788a] The eval scorecard. Platform reach only — it names
            // internal flows, budgets, and personas.
            <RequireCapability capability="platform:manage_tenants" scope="platform">
              <Evals />
            </RequireCapability>
          }
        />
        <Route
          path="/atlas"
          element={
            // Platform surface-area map. Open to tenant admins, who see the
            // executive-summary deck only — PlatformAtlas gates the Overview
            // and Roadmap inventories on platform reach. Same tenant:manage
            // bar as /settings; the sidebar entry uses it too.
            <RequireCapability capability="tenant:manage">
              <PlatformAtlas />
            </RequireCapability>
          }
        />
        <Route
          path="/classification"
          element={
            // Platform catalog — classification:manage at PLATFORM scope. Scope
            // is load-bearing: tenant_admin also holds classification:manage,
            // but only platform reach may write the global catalog.
            <RequireCapability
              capability="classification:manage"
              scope="platform"
            >
              <Classification />
            </RequireCapability>
          }
        />
        {/* S-320c — tenant-scoped counterpart. platform_admin manages the
            platform catalog at /classification; this tenant catalog uses
            scope="tenant" so it requires an own studio (excludes platform_admin,
            who has none), keeping the two surfaces distinct — the capability
            equivalent of the old tenant_admin-only allowlist. */}
        <Route
          path="/settings/classification"
          element={
            <RequireCapability
              capability="classification:manage"
              scope="tenant"
            >
              <TenantClassification />
            </RequireCapability>
          }
        />
        {/* S-339 — Perks Register: the deal-workflow command center over the
            S-336 routes. tenant_admin (their own tenant) or platform_admin. */}
        <Route
          path="/deals/:dealId"
          element={
            <RequireCapability capability="tenant:manage">
              <PerksRegister />
            </RequireCapability>
          }
        />
        {/* S-210 — Template authoring + lifecycle management. Reachable
            from both the "Tenant admin" group (tenant_admin → L2) and
            the PLATFORM group (platform_admin → L1). RequireRole
            minRole=tenant_admin gates both; the screen itself filters
            visible templates by the caller's layer scope. */}
        <Route
          path="/template-management"
          element={
            <RequireCapability capability="template:author">
              <TemplateManagement />
            </RequireCapability>
          }
        />
        {/* [S-721] Browse + import + exclude + rate — management surface
            sitting between the read-only Certified Library and Template
            Management's edit/certify flow. Same capability gate as import()
            itself (template:author). */}
        <Route
          path="/marketplace"
          element={
            <RequireCapability capability="template:author">
              <Marketplace />
            </RequireCapability>
          }
        />
        {/* [S-211] Bulk upload — drop multiple files, auto-extract, submit each
            as a draft using shared default metadata. */}
        <Route
          path="/template-management/bulk"
          element={
            <RequireCapability capability="template:author">
              <TemplateBulkUpload />
            </RequireCapability>
          }
        />
        <Route
          path="/certification-queue"
          element={
            <RequireCapability capability="template:certify">
              <CertificationQueue />
            </RequireCapability>
          }
        />
        <Route
          path="/admin/tenants"
          element={
            <RequireCapability
              capability="platform:manage_tenants"
              scope="platform"
            >
              <Tenants />
            </RequireCapability>
          }
        />
        <Route
          path="/admin/tenants/:tenantId/users"
          element={
            <RequireCapability capability="tenant:manage">
              <TenantUsers />
            </RequireCapability>
          }
        />
        <Route path="*" element={<NotYetBuilt screen="Unknown" story="—" />} />
        </Routes>
      </AppShell>
    </ActiveTenantProvider>
  );
}

// Hook used by <App> to apply tenant branding to <html> on boot. Lives
// in its own function so the App body stays focused on routing. Reads
// session studioId → tenantId, fetches preferences, and pushes the
// branding into document attributes (`data-accent`, `data-theme`) for
// CSS to react to. Falls back to defaults when there's no tenant or
// the query is still pending — applyBrandingToDocument is idempotent.
function useTenantBrandingBoot(): void {
  const session = useSession();
  const tenantId =
    session?.studioId !== null && session?.studioId !== undefined
      ? `tenant-${session.studioId}`
      : "";
  const prefsQuery = useTenantPreferences(tenantId);
  const branding = prefsQuery.data?.branding ?? null;

  useEffect(() => {
    if (branding === null) return;
    applyBrandingToDocument(branding.primaryColor, branding.darkMode);
  }, [branding?.primaryColor, branding?.darkMode]);
}

// S-411 — renders the branding boot INSIDE ActiveTenantProvider so it reads the
// effective (active-tenant) session and re-applies branding when the user
// switches workspace. Returns nothing; it's a side-effect-only component.
function BrandingBoot(): null {
  useTenantBrandingBoot();
  return null;
}
